"use client"

/**
 * Live preview of the user's watering schedule. Renders alongside the form
 * (right rail on desktop, above-the-fold card on mobile) and re-renders on
 * every input change so the schedule is the design, not a thing you wait for.
 */

import type { WateringScheduleResult } from "@/lib/watering-calculator"
import { useEffect, useState } from "react"
import { buildScheduleDays, type RestrictionId } from "@/lib/watering-schedule-days"
import { buildWateringScheduleIcs } from "@/lib/watering-ics"
import { trackEvent } from "@/lib/analytics"

const W_INK = "#111827"
const W_INK2 = "#374151"
const W_RULE = "#e5e7eb"
const W_BRAND = "#16a34a"
const W_BRAND_700 = "#15803d"
const W_BRAND_50 = "#f0fdf4"
const W_MUTED = "#6b7280"
const W_MUTED2 = "#9ca3af"

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"]
const DAY_FULL = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
]

function fmtRunTime(minutes: number) {
  const rounded = Math.round(minutes)
  if (rounded < 60) return `${rounded} min`
  const hours = Math.floor(rounded / 60)
  const mins = rounded % 60
  return mins === 0 ? `${hours} hr` : `${hours} hr ${mins} min`
}

export function formatNextScheduledWateringDate(
  days: Array<{ on: boolean }>,
  deferOneWeek: boolean,
  referenceDate: Date,
) {
  const today = new Date(referenceDate)
  const firstOffset = deferOneWeek ? 7 : 0
  for (let offset = firstOffset; offset < firstOffset + 7; offset += 1) {
    const dayIndex = (today.getDay() + offset) % 7
    if (!days[dayIndex]?.on) continue
    const date = new Date(today)
    date.setDate(today.getDate() + offset)
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    })
  }
  return null
}

export function getUpcomingScheduleDays<T extends { on: boolean }>(
  days: T[],
  referenceDay: number,
  limit = 3,
) {
  return days
    .map((day, index) => ({ day, offset: (index - referenceDay + 7) % 7 }))
    .filter(({ day }) => day.on)
    .sort((a, b) => a.offset - b.offset)
    .slice(0, limit)
    .map(({ day }) => day)
}

export interface WateringPreviewProps {
  result: WateringScheduleResult
  restrictionId: RestrictionId
  /** Toggle compact mode for the mobile above-fold card. */
  compact?: boolean
}

export function WateringPreview({ result, restrictionId, compact = false }: WateringPreviewProps) {
  const [localDate, setLocalDate] = useState<Date | null>(null)
  useEffect(() => setLocalDate(new Date()), [])
  const sessionGallons = result.sessionsPerWeek > 0
    ? Math.round(result.weeklyGallons / result.sessionsPerWeek)
    : 0
  const days = buildScheduleDays({
    sessionsPerWeek: result.sessionsPerWeek,
    gallonsPerSession: sessionGallons,
    runMinutes: result.sessionDuration,
    restrictionId,
  })

  const max = Math.max(1, ...days.map(d => d.gallons))
  const today = localDate?.getDay() ?? -1
  const upcoming = localDate
    ? getUpcomingScheduleDays(days, localDate.getDay())
    : []
  const nextWateringDate = localDate
    ? formatNextScheduledWateringDate(days, result.skipThisWeek, localDate)
    : null
  const gallonsNeededThisWeek = result.skipThisWeek ? 0 : result.weeklyGallons
  const isRainSkip = result.skipThisWeek && result.rainfallSubtracted > 0
  const hasDryWeekBaseline = result.sessionsPerWeek > 0 && result.weeklyGallons > 0
  const multiZone = result.zoneCount > 1
  const zoneNoun = result.zoneCount === 1 ? "zone" : "zones"
  const zoneSpecific = result.applicationRateSource === "catch-can-by-zone"
  const measuredRate = result.applicationRateSource !== "system-estimate"
  const zoneFastest = zoneSpecific
    ? [...result.zoneRunTimes].sort((a, b) => a.runMinutesPerSession - b.runMinutesPerSession)[0]
    : null
  const zoneSlowest = zoneSpecific
    ? [...result.zoneRunTimes].sort((a, b) => b.runMinutesPerSession - a.runMinutesPerSession)[0]
    : null
  const perRunLabel = zoneSpecific
    ? `${result.sessionDuration} min avg/zone`
    : multiZone
      ? `${result.sessionDuration} min each zone`
      : `${result.sessionDuration} min total`
  const targetInchesPerSession = result.targetInchesPerSession ??
    (result.sessionsPerWeek > 0 ? result.soilAdjustedWater / result.sessionsPerWeek : 0)

  // The schedule below always renders the user's typical weekly pattern.
  // Rainfall context is surfaced here so a rainy week reads as a deliberate
  // "skip" or "adjust this week" callout rather than a silent shrinking of
  // the numbers.
  const showRainfallBanner = result.skipThisWeek || result.rainfallSubtracted > 0
  const rainfallBanner = showRainfallBanner ? (
    <div
      style={{
        marginBottom: 12,
        background: W_BRAND_50,
        border: `1px solid ${W_BRAND}`,
        borderRadius: 8,
        padding: "10px 12px",
        fontSize: 13,
        color: W_BRAND_700,
        lineHeight: 1.45,
      }}
      role="status"
    >
      {result.skipThisWeek ? (
        result.rainfallSubtracted > 0 ? (
          <>
            <strong>0 gallons needed this week.</strong>{" "}
            We measured {result.rainfallSubtracted.toFixed(2)}″ of rain in your area
            over the past 7 days, which already covers your weekly need.
            {nextWateringDate ? ` Next scheduled watering: ${nextWateringDate}.` : ""}
          </>
        ) : (
          <>
            <strong>No watering needed this season.</strong>{" "}
            {result.grassType} is dormant in {result.season}, so irrigation is not
            recommended until the next growing season.
          </>
        )
      ) : (
        <>
          <strong>Adjust this week.</strong>{" "}
          We measured {result.rainfallSubtracted.toFixed(2)}″ of rain in your area
          over the past 7 days. Your typical schedule is below; consider running
          shorter sessions or skipping a day to compensate.
        </>
      )}
    </div>
  ) : null

  if (compact) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-elevation-1">
        {rainfallBanner}
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: W_BRAND_700,
            marginBottom: 6,
          }}
        >
          This week
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
          <span
            style={{
              fontSize: 26,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: "-0.025em",
              color: W_INK,
            }}
          >
            {gallonsNeededThisWeek.toLocaleString()}
          </span>
          <span style={{ fontSize: 13, color: W_MUTED }}>gal</span>
          {!result.skipThisWeek && typeof result.weeklyCost === "number" && (
            <span
              style={{
                fontSize: 12,
                color: W_MUTED,
                marginLeft: "auto",
              }}
            >
              ${result.weeklyCost.toFixed(2)}/wk
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: W_MUTED, marginBottom: multiZone ? 4 : 12 }}>
          {result.skipThisWeek
            ? isRainSkip
              ? "Rain covered your watering need"
              : `${result.grassType} is dormant in ${result.season}`
            : `${result.sessionsPerWeek}× per week · ${perRunLabel}`}
        </div>
        {result.skipThisWeek && hasDryWeekBaseline && (
          <div style={{ fontSize: 12, color: W_INK2, marginBottom: 12, lineHeight: 1.5 }}>
            <strong>Dry-week baseline:</strong> {result.weeklyGallons.toLocaleString()} gal across {result.sessionsPerWeek} sessions.
            {nextWateringDate ? ` Next scheduled watering: ${nextWateringDate}.` : ""}
          </div>
        )}
        {multiZone && (
          <div style={{ fontSize: 12, color: W_MUTED, marginBottom: 12 }}>
            {fmtRunTime(result.controllerRunMinutesPerSession)} total if all {result.zoneCount} zones run in sequence.
          </div>
        )}
        <div
          style={{
            background: W_BRAND_50,
            border: `1px solid ${W_RULE}`,
            borderRadius: 8,
            padding: "9px 10px",
            fontSize: 12,
            color: W_INK2,
            lineHeight: 1.45,
            marginBottom: 12,
          }}
        >
          {zoneSpecific && zoneFastest && zoneSlowest ? (
            <>
              <strong>Catch-can mode:</strong> faster zones run less. Zone {zoneFastest.zoneNumber} is{" "}
              {zoneFastest.runMinutesPerSession} min, while zone {zoneSlowest.zoneNumber} is{" "}
              {zoneSlowest.runMinutesPerSession} min.
            </>
          ) : (
            <>
              <strong>{result.zoneCount} {zoneNoun}</strong> means {multiZone ? "each zone" : "the single area"} gets the
              listed minutes. {measuredRate
                ? "Your measured catch-can rate is driving that time."
                : `Each zone averages ${result.averageZoneSize.toLocaleString()} sq ft.`}
            </>
          )}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 4,
          }}
        >
          {days.map((d, i) => {
            const h = d.gallons > 0 ? Math.max(6, (d.gallons / max) * 50) : 0
            const isToday = i === today
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  padding: "4px 0",
                  background: isToday ? W_BRAND_50 : "transparent",
                  borderRadius: 6,
                }}
              >
                <div style={{ height: 56, display: "flex", alignItems: "flex-end" }}>
                  {d.on ? (
                    <div
                      style={{
                        width: 16,
                        height: h,
                        borderRadius: 3,
                        background: W_BRAND,
                        transition: "height 220ms var(--ease-standard)",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 16,
                        height: 3,
                        borderRadius: 1.5,
                        background: W_RULE,
                      }}
                    />
                  )}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: isToday ? 700 : 500,
                    color: isToday ? W_BRAND_700 : d.on ? W_INK2 : W_MUTED2,
                  }}
                >
                  {DAY_LABELS[i]}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex flex-col gap-5 rounded-2xl border border-gray-200 bg-white px-6 pt-6 pb-5 shadow-elevation-2"
      style={{ color: W_INK }}
    >
      {rainfallBanner}
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: W_BRAND_700,
            marginBottom: 10,
          }}
        >
          This week
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 38,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: "-0.025em",
              color: W_INK,
            }}
          >
            {gallonsNeededThisWeek.toLocaleString()}
          </span>
          <span style={{ fontSize: 14, color: W_MUTED }}>gal needed</span>
        </div>
        <div style={{ fontSize: 13, color: W_MUTED, marginTop: 8 }}>
          {result.skipThisWeek
            ? isRainSkip
              ? "Rain covered your watering need"
              : `${result.grassType} is dormant in ${result.season}`
            : `${result.sessionsPerWeek}× this week · ${perRunLabel} · ${targetInchesPerSession.toFixed(2)}″ per run`}
        </div>
        {result.skipThisWeek && hasDryWeekBaseline && (
          <div style={{ fontSize: 13, color: W_INK2, marginTop: 8, lineHeight: 1.5 }}>
            <strong>Dry-week baseline:</strong> {result.weeklyGallons.toLocaleString()} gal across {result.sessionsPerWeek} sessions.
            {nextWateringDate ? ` Next scheduled watering: ${nextWateringDate}.` : ""}
          </div>
        )}
      </div>

      <div
        style={{
          background: W_BRAND_50,
          border: `1px solid ${W_RULE}`,
          borderRadius: 10,
          padding: "12px 14px",
          fontSize: 13,
          color: W_INK2,
          lineHeight: 1.5,
        }}
      >
        {zoneSpecific && zoneFastest && zoneSlowest ? (
          <>
            <strong>Catch-can mode</strong> uses each zone's measured precipitation rate. Faster zones run less:
            zone {zoneFastest.zoneNumber} is {zoneFastest.runMinutesPerSession} minutes, while zone{" "}
            {zoneSlowest.zoneNumber} is {zoneSlowest.runMinutesPerSession} minutes. Total controller time is{" "}
            <strong>{fmtRunTime(result.controllerRunMinutesPerSession)}</strong> per watering day.
          </>
        ) : (
          <>
            <strong>{result.zoneCount} {zoneNoun}</strong> means {multiZone ? "each zone" : "the single coverage area"} gets{" "}
            {result.sessionDuration} minutes per watering day. {multiZone ? (
              <>
                If your controller runs them one after another, plan on{" "}
                <strong>{fmtRunTime(result.controllerRunMinutesPerSession)}</strong> total per watering day and{" "}
                <strong>{fmtRunTime(result.controllerRunMinutesPerWeek)}</strong> across the week.
              </>
            ) : (
              <>
                If your system has multiple sprinkler stations, set the zone count so the total controller time is clear.
              </>
            )}
          </>
        )}
      </div>

      <div style={{ height: 1, background: W_RULE }} />

      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: W_INK2,
            marginBottom: 12,
          }}
        >
          {result.skipThisWeek
            ? hasDryWeekBaseline ? "Dry-week baseline" : "No watering this season"
            : "This week"}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 6,
          }}
        >
          {days.map((d, i) => {
            const h = d.gallons > 0 ? Math.max(8, (d.gallons / max) * 88) : 0
            const isToday = i === today
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 0",
                  background: isToday ? W_BRAND_50 : "transparent",
                  borderRadius: 8,
                }}
              >
                <div style={{ height: 100, display: "flex", alignItems: "flex-end" }}>
                  {d.on ? (
                    <div
                      style={{
                        width: 20,
                        height: h,
                        borderRadius: 4,
                        background: W_BRAND,
                        transition: "height 220ms var(--ease-standard)",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 20,
                        height: 4,
                        borderRadius: 2,
                        background: W_RULE,
                      }}
                    />
                  )}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: isToday ? 700 : 500,
                    color: isToday ? W_BRAND_700 : W_MUTED,
                  }}
                >
                  {DAY_LABELS[i]}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 500,
                    color: d.on ? W_INK2 : W_MUTED2,
                  }}
                >
                  {d.on ? `${d.gallons}` : "—"}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {upcoming.length > 0 && (
        <>
          <div style={{ height: 1, background: W_RULE }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: W_INK2,
              }}
            >
              {nextWateringDate ? `Next watering · ${nextWateringDate}` : "Next watering"}
            </div>
            {upcoming.map((d, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  fontSize: 13.5,
                  color: W_INK2,
                  paddingBottom: 8,
                  borderBottom:
                    i < upcoming.length - 1 ? `1px solid ${W_RULE}` : "none",
                }}
              >
                <span style={{ width: 80, color: W_INK, fontWeight: 600 }}>
                  {DAY_FULL[d.day]}
                </span>
                <span style={{ width: 64, color: W_MUTED }}>{d.time}</span>
                <span style={{ flex: 1, color: W_MUTED }}>
                  {d.minutes} min{zoneSpecific ? " avg/zone" : multiZone ? "/zone" : ""}
                </span>
                <span style={{ color: W_INK, fontWeight: 600 }}>
                  {d.gallons.toLocaleString()} gal
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {(typeof result.weeklyCost === "number" ||
        typeof result.monthlyCost === "number" ||
        typeof result.seasonalCost === "number") && (
        <div
          style={{
            background: W_BRAND_50,
            borderRadius: 8,
            padding: "12px 14px",
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
          }}
        >
          <Stat label="Per week" value={fmtCost(result.weeklyCost)} />
          <Stat label="Per month" value={fmtCost(result.monthlyCost)} />
          <Stat label="5-mo season" value={fmtCost(result.seasonalCost)} />
        </div>
      )}

      {!compact && (
        <AddToCalendarButton result={result} restrictionId={restrictionId} />
      )}
    </div>
  )
}

/**
 * Turns the schedule into a downloadable .ics so the user can leave with their
 * watering days, not just read them — the canonical "make the result
 * actionable" affordance best-in-class utility tools have at the result moment.
 * Hidden when there's nothing to schedule (dormant season / full rainfall skip).
 */
function AddToCalendarButton({
  result,
  restrictionId,
}: {
  result: WateringScheduleResult
  restrictionId: RestrictionId
}) {
  if (result.sessionsPerWeek <= 0 || result.skipThisWeek) return null

  const handleClick = () => {
    const out = buildWateringScheduleIcs(result, restrictionId)
    if (!out) return
    const blob = new Blob([out.ics], { type: "text/calendar;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = out.filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
    trackEvent("watering_calendar_exported", {
      grass_type: result.grassType,
      restriction: restrictionId,
      sessions_per_week: result.sessionsPerWeek,
    })
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        marginTop: 14,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "11px 14px",
        background: "#fff",
        border: `1px solid ${W_BRAND}`,
        borderRadius: 8,
        color: W_BRAND_700,
        fontSize: 13.5,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M8 2v4M16 2v4M3 10h18" />
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M12 14v4M10 16h4" />
      </svg>
      Add watering days to calendar
    </button>
  )
}

function fmtCost(n: number | undefined) {
  if (typeof n !== "number") return "—"
  return n >= 100 ? `$${Math.round(n).toLocaleString()}` : `$${n.toFixed(2)}`
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: W_MUTED,
          marginBottom: 2,
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 16,
          color: W_INK,
          fontWeight: 700,
          letterSpacing: "-0.01em",
        }}
      >
        {value}
      </div>
    </div>
  )
}
